import { animate, style, transition, trigger } from "@angular/animations";
import { ChangeDetectionStrategy, Component, OnInit } from "@angular/core";
import { MainState } from "@app/store/state";
import { GroupInterface } from "@core/interfaces/group.interface";
import { UserInterface } from "@core/interfaces/user.interface";
import { ClassicRoutesService } from "@core/services/classic-routes.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { selectCurrentUser } from "@features/account/store/auth.selectors";
import { Store } from "@ngrx/store";
import { FieldType, FieldTypeConfig } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

interface GroupSelectViewModel {
  options: { value: number | null; label: string }[];
  hasGroups: boolean;
}

@Component({
  selector: "astrobin-progressive-disclosure-group-select",
  templateUrl: "./progressive-disclosure-group-select.component.html",
  styleUrls: ["./progressive-disclosure-group-select.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger("expandCollapse", [
      transition(":enter", [
        style({ opacity: 0, transform: "translateY(-10px)" }),
        animate("200ms ease-out", style({ opacity: 1, transform: "translateY(0)" }))
      ])
    ])
  ]
})
export class ProgressiveDisclosureGroupSelectComponent extends FieldType<FieldTypeConfig> implements OnInit {
  isExpanded = false;
  currentUser$: Observable<UserInterface>;
  viewModel$: Observable<GroupSelectViewModel>;
  groupsPageUrl: string;
  private originalDescription: string;

  constructor(
    private readonly store$: Store<MainState>,
    private readonly translateService: TranslateService,
    private readonly classicRoutesService: ClassicRoutesService,
    private readonly utilsService: UtilsService
  ) {
    super();
  }

  ngOnInit(): void {
    this.currentUser$ = this.store$.select(selectCurrentUser);
    this.groupsPageUrl = this.classicRoutesService.GROUPS;

    // Store original description and initially hide label and description
    this.originalDescription = this.field.props.description;
    this.field.props.hideLabel = true;
    this.field.props.description = null;

    // Call the onReady callback if provided
    if (this.props.onReady) {
      this.props.onReady(this);
    }

    this.viewModel$ = this.currentUser$.pipe(
      map(user => {
        const hasGroups = !!(user && user.astrobinGroups && user.astrobinGroups.length > 0);

        const options: { value: number | null; label: string }[] = [
          { value: null, label: this.translateService.instant("Public (no group)") }
        ];

        if (hasGroups) {
          // Filter out groups without forums and map to options
          const groupOptions = user.astrobinGroups
            .filter((group: GroupInterface) => group.forum !== null && group.forum !== undefined)
            .map((group: GroupInterface) => ({
              value: group.forum,
              label: group.name
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

          options.push(...groupOptions);
        }

        return {
          options,
          hasGroups
        };
      })
    );

    // Check if we should expand based on initial value
    if (this.formControl.value) {
      this.isExpanded = true;
      this.field.props.hideLabel = false;
      this.field.props.description = this.originalDescription;
    }

    // Also listen for value changes to auto-expand
    this.formControl.valueChanges.subscribe(value => {
      if (value && !this.isExpanded) {
        this.isExpanded = true;
        this.field.props.hideLabel = false;
        this.field.props.description = this.originalDescription;
      }
    });
  }

  toggleExpanded(): void {
    // Only allow expanding, not collapsing
    if (!this.isExpanded) {
      this.isExpanded = true;

      // Show the label and description when expanded
      this.field.props.hideLabel = false;
      this.field.props.description = this.originalDescription;

      // Focus the select element when expanding
      this.utilsService.delay(100).subscribe(() => {
        const selectElement = document.querySelector(".group-select-dropdown ng-select input");
        if (selectElement) {
          (selectElement as HTMLElement).focus();
        }
      });
    }
  }

  onGroupChange(forumId: number | null): void {
    // Update the form control value
    this.formControl.setValue(forumId);

    // If props has an onChange callback, call it
    if (this.props.onChange) {
      this.props.onChange(forumId);
    }
  }

  collapse(): void {
    // Reset to collapsed state
    this.isExpanded = false;
    this.field.props.hideLabel = true;
    this.field.props.description = null;
  }
}
